"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
    LayoutDashboard,
    MapPinned,
    Sprout,
    Cpu,
    Zap,
    Activity,
    ClipboardList,
    Leaf,
    Menu,
    X,
    LogOut,
    Rocket,
    Settings,
} from "lucide-react";

const menuGroups = [
    {
        label: "현황",
        menus: [{ name: "대시보드", href: "/", icon: LayoutDashboard }],
    },
    {
        label: "농장 운영",
        menus: [
            { name: "농장", href: "/farms", icon: MapPinned },
            { name: "재배 구역", href: "/zones", icon: Sprout },
            { name: "자동화", href: "/automation", icon: Zap },
        ],
    },
    {
        label: "구성",
        menus: [
            { name: "재배 프로파일", href: "/cultivation-profiles", icon: Leaf },
            { name: "정책", href: "/policies", icon: ClipboardList },
            { name: "노드", href: "/nodes", icon: Cpu },
            { name: "배포", href: "/deployments", icon: Rocket },
        ],
    },
    {
        label: "모니터링",
        menus: [{ name: "로그", href: "/logs", icon: Activity }],
    },
    {
        label: "시스템",
        menus: [{ name: "설정", href: "/settings", icon: Settings }],
    },
];

export function Sidebar({
    isDevelopmentEnvironment,
    coreVersion,
}: {
    isDevelopmentEnvironment: boolean;
    coreVersion: { version: string; buildId: string | null };
}) {
    const pathname = usePathname();
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center justify-between border-b border-slate-100 bg-white/95 px-4 backdrop-blur lg:hidden">
                <Brand compact isDevelopmentEnvironment={isDevelopmentEnvironment} coreVersion={coreVersion} />
                <button
                    type="button"
                    aria-label={mobileOpen ? "메뉴 닫기" : "메뉴 열기"}
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((open) => !open)}
                    className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 text-slate-600"
                >
                    {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </header>

            <aside
                className={`fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-slate-50 p-6 lg:flex ${isDevelopmentEnvironment ? "border-amber-200" : "border-slate-100"}`}
            >
                <Brand isDevelopmentEnvironment={isDevelopmentEnvironment} coreVersion={coreVersion} />
                <MenuGroups pathname={pathname} />
                <LogoutButton />
            </aside>

            {mobileOpen && (
                <button
                    type="button"
                    aria-label="메뉴 닫기"
                    onClick={() => setMobileOpen(false)}
                    className="fixed inset-0 z-40 bg-slate-950/30 backdrop-blur-[1px] lg:hidden"
                />
            )}

            <aside
                className={[
                    "fixed inset-y-0 left-0 z-50 flex w-[min(82vw,20rem)] flex-col border-r border-slate-100 bg-slate-50 p-5 shadow-2xl transition-transform duration-200 lg:hidden",
                    mobileOpen ? "translate-x-0" : "-translate-x-full",
                ].join(" ")}
                aria-hidden={!mobileOpen}
            >
                <div className="flex items-center justify-between">
                    <Brand compact isDevelopmentEnvironment={isDevelopmentEnvironment} coreVersion={coreVersion} />
                    <button
                        type="button"
                        aria-label="메뉴 닫기"
                        onClick={() => setMobileOpen(false)}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl text-slate-500 hover:bg-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <MenuGroups pathname={pathname} onNavigate={() => setMobileOpen(false)} />
                <LogoutButton />
            </aside>
        </>
    );
}

function LogoutButton() {
    const [loggingOut, setLoggingOut] = useState(false);

    const logout = async () => {
        setLoggingOut(true);
        try {
            await fetch("/api/auth/logout", { method: "POST" });
        } finally {
            window.location.assign("/login");
        }
    };

    return (
        <button
            type="button"
            onClick={() => void logout()}
            disabled={loggingOut}
            className="mt-4 flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-slate-400 transition hover:bg-white hover:text-rose-600 disabled:opacity-50"
        >
            <LogOut className="h-4 w-4" />
            로그아웃
        </button>
    );
}

function Brand({
    compact = false,
    isDevelopmentEnvironment,
    coreVersion,
}: {
    compact?: boolean;
    isDevelopmentEnvironment: boolean;
    coreVersion: { version: string; buildId: string | null };
}) {
    return (
        <div className={compact ? "flex items-center gap-2.5" : "mb-10 flex items-center gap-3 px-2"}>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 p-1.5 shadow-lg shadow-emerald-200">
                <Image
                    src="/brand/ciat-symbol.png"
                    alt="C:AT 씨앗"
                    width={44}
                    height={44}
                    className="h-full w-full scale-110 object-contain brightness-0 invert"
                    priority
                />
            </div>

            <div>
                <div className="flex items-center gap-2">
                    <div className={`${compact ? "text-base" : "text-xl"} font-black tracking-tighter text-slate-800`}>
                        HYDRO-CLOUD
                    </div>
                    {isDevelopmentEnvironment && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black tracking-wider text-amber-700 ring-1 ring-inset ring-amber-200">
                            DEV
                        </span>
                    )}
                </div>
                <div
                    className={`${compact ? "text-[9px]" : "text-[11px]"} font-bold uppercase tracking-wider text-slate-300`}
                >
                    Core v{coreVersion.version}
                    {coreVersion.buildId ? ` · ${coreVersion.buildId}` : ""}
                </div>
            </div>
        </div>
    );
}

function MenuGroups({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
    return (
        <nav className="mt-8 flex-1 space-y-6 overflow-y-auto lg:mt-0">
            {menuGroups.map((group) => (
                <div key={group.label}>
                    <div className="mb-2 px-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
                        {group.label}
                    </div>
                    <div className="space-y-1">
                        {group.menus.map((menu) => {
                            const isActive =
                                menu.href === "/"
                                    ? pathname === "/"
                                    : pathname === menu.href || pathname.startsWith(`${menu.href}/`);

                            const Icon = menu.icon;

                            return (
                                <Link
                                    key={menu.href}
                                    href={menu.href}
                                    onClick={onNavigate}
                                    className={[
                                        "flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-bold transition-all",
                                        isActive
                                            ? "border border-slate-100 bg-white text-blue-600 shadow-sm"
                                            : "text-slate-400 hover:bg-white/50 hover:text-slate-600",
                                    ].join(" ")}
                                >
                                    <Icon
                                        className={["h-5 w-5", isActive ? "text-blue-600" : "text-slate-300"].join(" ")}
                                    />
                                    {menu.name}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            ))}
        </nav>
    );
}
